import { z } from 'zod'

export const ledgerReportSchema = z.object({
  rows: z.array(
    z.object({
      TxnDate: z.string().or(z.null()),
      EntryDate: z.string().or(z.null()),
      TransactionID: z.string().or(z.null()),
      Category: z.string().or(z.null()),
      'Asset/Token': z.string().or(z.null()),
      Asset: z.string().or(z.null()),
      AssetAmount: z.string().or(z.null()),
      'Account Id': z.string(),
      'Account Name': z.string().or(z.null()),
      CreditsFiat: z.string().or(z.number()),
      DebitsFiat: z.string().or(z.number()),
      Counterparty: z.string().or(z.null()),
      Journal_ID: z.string().or(z.null()),
      Memo: z.string().or(z.null()),
    })
  ),
})

export const assetReportSchema = z.object({
  headers: z.array(z.string()),
  rows: z.array(z.array(z.string().or(z.null()))),
  num_rows: z.number().nullable().optional(),
})

export const impairedAssetsSchema = z.object({
  result: z.array(z.string()),
})

export const updateImpairedAssetsSchema = z.object({
  result: z.string().or(z.array(z.string())),
})

export const reportPaginationSchema = z.object({
  next_token: z.string().or(z.null()).optional(),
  query_execution_id: z.string().optional(),
})

export const impairmentSummaryReportSchema = z
  .object({
    rows: z.array(
      z.object({
        asset: z.string(),
        quantity: z.string(),
        costbasis: z.string(),
        beginningadjustmentvalue: z.string(),
        endingadjustmentvalue: z.string(),
        periodadjustment: z.string(),
        totaladjustment: z.string(),
        lowestperiodprice: z.string().or(z.null()),
        lowestpricedatetime: z.string().or(z.null()),
      })
    ),
  })
  .merge(reportPaginationSchema)

export const impairmentLotLevelReportSchema = z
  .object({
    rows: z.array(
      z.object({
        lotId: z.string(),
        asset: z.string(),
        quantity: z.string(),
        costBasis: z.string(),
        acquisitionDate: z.string(),
        adjustmentBeginDate: z.string(),
        adjustmentEndDate: z.string(),
        beginningAdjustmentValue: z.string(),
        endingAdjustmentValue: z.string(),
        periodAdjustment: z.string(),
        totalAdjustment: z.string(),
        lowestPeriodPrice: z.string().or(z.null()),
        lowestPriceDatetime: z.string().or(z.null()),
      })
    ),
  })
  .merge(reportPaginationSchema)

export const significantHoldingsReportSchema = z
  .object({
    by_asset: z.record(
      z.string(),
      z.object({
        amount: z.number(),
        cost_basis: z.number().nullable(),
        value: z.number().nullable(),
      })
    ),
  })
  .merge(reportPaginationSchema)

export const gainLossReportRowSchema = z.object({
  amount: z.string(),
  asset: z.string(),
  buyPrice: z.string(),
  buyTimestamp: z.string(),
  buyTransactionID: z.string(),
  costBasis: z.string(),
  gain: z.string(),
  proceeds: z.string(),
  sellPrice: z.string(),
  sellSourceAddr: z.string(),
  sellTimestamp: z.string(),
  sellTransactionID: z.string(),
})

export const gainLossReportSchema = z
  .object({
    rows: z.array(gainLossReportRowSchema),
  })
  .merge(reportPaginationSchema)

export const rollingGainLossReportSchema = z
  .object({
    rows: z.record(z.string(), z.number()),
  })
  .merge(reportPaginationSchema)

export const gainlossSummaryRowSchema = z.object({
  asset: z.string(),
  gain: z.string(),
})

export const gainlossSummarySchema = z.object({
  rows: z.array(gainlossSummaryRowSchema),
})

/**
 * 'total' is a guaranteed key in the object provided that there are
 * other categories as well. This object will have either 0 or 2+ keys.
 */
const reconRollForwardValuesSchema = z.record(z.string(), z.number())

export const reconciliationRollForwardReportSchema = z.object({
  data: z.object({
    additions: reconRollForwardValuesSchema,
    dispositions: reconRollForwardValuesSchema,
    fair_value_as_of_from_date: z.number(),
    fair_value_as_of_to_date: z.number(),
    gainloss: reconRollForwardValuesSchema,
  }),
})

export const pricesDailySummarySchema = z
  .object({
    rows: z.array(
      z.object({
        date: z.string(),
        asset: z.string(),
        average_price: z.string(),
        intraday_low_price: z.string(),
        end_of_day_price: z.string(),
        end_of_day_source: z.string().nullable().optional(),
        intraday_low_timestamp: z.string().optional(),
      })
    ),
  })
  .merge(reportPaginationSchema)

export const qaRollForwardRowSchema = z.object({
  asset: z.string().nullable(),
  source: z.string().nullable(),
  bucket: z.string().nullable(),
  amount: z.string().nullable(),
  fiat_amount: z.string().nullable(),
})

export const qaRollForwardSchema = z
  .object({
    rows: z.array(qaRollForwardRowSchema),
  })
  .merge(reportPaginationSchema)

export const qaAssetDataSchema = z
  .object({
    headers: z.array(z.string()),
    rows: z.array(z.array(z.string().or(z.null()))),
  })
  .merge(reportPaginationSchema)

export const qaBalanceRowSchema = z.object({
  timestamp: z.string(),
  txn_id: z.string(),
  asset: z.string(),
  category: z.string(),
  source: z.string(),
  in_amount: z.string(),
  in_fiat_amount: z.string(),
  out_amount: z.string(),
  out_fiat_amount: z.string(),
  running_balance: z.string(),
  running_cost_basis: z.string(),
  gain: z.string(),
})

export const qaBalanceSchema = z
  .object({
    rows: z.array(qaBalanceRowSchema),
  })
  .merge(reportPaginationSchema)

export const qaSignificantHoldingsRowSchema = z.object({
  asset: z.string(),
  amount: z.string(),
  fiat_amount: z.string(),
  cost_basis: z.string(),
})

export const qaSignificantHoldingsSchema = z
  .object({
    rows: z.array(qaSignificantHoldingsRowSchema),
  })
  .merge(reportPaginationSchema)

// Add QA Global Entity Holdings schemas
export const qaGlobalEntityHoldingsRowSchema = z.object({
  parent_organization: z.string(),
  organization: z.string(),
  asset: z.string(),
  amount: z.string(),
  cost_basis: z.string(),
  fiat_amount: z.string(),
  date: z.string().optional(),
})

export const qaGlobalEntityHoldingsSchema = z
  .object({
    rows: z.array(qaGlobalEntityHoldingsRowSchema),
  })
  .merge(reportPaginationSchema)

// QA Global Asset Holdings — API returns total_cost_basis (not cost_basis); normalize for consumers.
export const qaGlobalAssetHoldingsRowSchema = z
  .object({
    asset: z.string(),
    amount: z.string(),
    fiat_amount: z.string(),
    total_cost_basis: z.string(),
    unrealized_gain_loss: z.string(),
    date: z.string().optional(),
  })
  .transform(({ total_cost_basis, date, ...rest }) => ({
    ...rest,
    cost_basis: total_cost_basis,
    ...(date !== undefined ? { date } : {}),
  }))

export const qaGlobalAssetHoldingsSchema = z
  .object({
    rows: z.array(qaGlobalAssetHoldingsRowSchema),
  })
  .merge(reportPaginationSchema)

export const significantHoldingsDetailedRowSchema = z.object({
  asset: z.string(),
  source: z.string(),
  amount: z.string(),
  fiat_amount: z.string(),
})

export const significantHoldingsDetailedSchema = z
  .object({
    rows: z.array(significantHoldingsDetailedRowSchema),
  })
  .merge(reportPaginationSchema)

export const qaFairMarketValueSummaryRowSchema = z.object({
  asset: z.string(),
  begin_value_all: z.string().nullable(),
  begin_value_held: z.string().nullable(),
  end_amount_held: z.string().nullable(),
  cost_basis_held: z.string().nullable(),
  end_value_held: z.string().nullable(),
  period_adjustment_held: z.string().nullable(),
  period_adjustment_disp: z.string().nullable(),
  period_adjustment_all: z.string().nullable(),
  cumulative_adjustment_held: z.string().nullable(),
})

export const qaFairMarketValueSummarySchema = z
  .object({
    rows: z.array(qaFairMarketValueSummaryRowSchema),
  })
  .merge(reportPaginationSchema)

export const fairMarketValueLotDispositionsRowSchema = z.object({
  amount: z.string().nullable(),
  asset: z.string().nullable(),
  begin_value: z.string().nullable(),
  buytimestamp: z.string().nullable(),
  buy_txn_id: z.string().nullable(),
  costbasis: z.string().nullable(),
  cumulative_adjustment: z.string().nullable(),
  disposal_txn_id: z.string().nullable(),
  end_value: z.string().nullable(),
  event_source: z.string().nullable(),
  lot_id: z.string().nullable(),
  selltimestamp: z.string().nullable(),
  value_adjustment: z.string().nullable(),
})

export const fairMarketValueLotDispositionsSchema = z
  .object({
    rows: z.array(fairMarketValueLotDispositionsRowSchema),
  })
  .merge(reportPaginationSchema)

export const fairMarketValueLotEndingInventoryRowSchema = z.object({
  buy_txn_id: z.string().nullable(),
  lot_id: z.string().nullable(),
  event_source: z.string().nullable(),
  asset: z.string().nullable(),
  begin_amount: z.string().nullable(),
  amount: z.string().nullable(),
  fiat_amount: z.string().nullable(),
  acquisition_date: z.string().nullable(),
  begin_period: z.string().nullable(),
  end_period: z.string().nullable(),
  begin_value: z.string().nullable(),
  end_value: z.string().nullable(),
  value_adjustment: z.string().nullable(),
  cumulative_adjustment: z.string().nullable(),
})

export const fairMarketValueLotEndingInventorySchema = z
  .object({
    rows: z.array(fairMarketValueLotEndingInventoryRowSchema),
  })
  .merge(reportPaginationSchema)

export const impairmentLotEndingInventoryRowSchema = z.object({
  asset: z.string(),
  buy_txn_id: z.string(),
  lot_id: z.string(),
  event_source: z.string(),
  begin_amount: z.string(),
  amount: z.string(),
  fiat_amount: z.string(),
  acquisition_date: z.string(),
  begin_period: z.string(),
  end_period: z.string(),
  begin_value: z.string().nullable(),
  end_value: z.string().nullable(),
  period_impairment: z.string().nullable(),
  lowest_lifetime_price: z.string().nullable(),
  lowest_lifetime_price_date_time: z.string().nullable(),
})

export const impairmentLotEndingInventorySchema = z
  .object({
    rows: z.array(impairmentLotEndingInventoryRowSchema),
  })
  .merge(reportPaginationSchema)

export const impairmentDispositionRowSchema = z.object({
  asset: z.string(),
  buy_txn_id: z.string(),
  lot_id: z.string(),
  event_source: z.string(),
  amount: z.string(),
  disposal_txn_id: z.string(),
  costbasis: z.string(),
  acquisition_date: z.string(),
  disposition_date: z.string(),
  begin_value: z.string().nullable(),
  end_value: z.string().nullable(),
  period_impairment: z.string().nullable(),
  lowest_lifetime_price: z.string().nullable(),
  lowest_lifetime_price_date_time: z.string().nullable(),
})

export const impairmentDispositionSchema = z
  .object({
    rows: z.array(impairmentDispositionRowSchema),
  })
  .merge(reportPaginationSchema)

export const qaImpairmentSummaryRowSchema = z.object({
  asset: z.string(),
  begin_value_all: z.string().nullable(),
  begin_value_held: z.string().nullable(),
  end_amount_held: z.string(),
  cost_basis_held: z.string().nullable(),
  end_value_held: z.string().nullable(),
  period_adjustment_held: z.string().nullable(),
  period_adjustment_disp: z.string().nullable(),
  total_impairment: z.string().nullable(),
})

export const qaImpairmentSummarySchema = z
  .object({
    rows: z.array(qaImpairmentSummaryRowSchema),
  })
  .merge(reportPaginationSchema)

export const fairMarketValueLotLevelRowSchema = z.object({
  acquisitionDate: z.string().nullable(),
  adjustmentBeginDate: z.string().nullable(),
  adjustmentEndDate: z.string().nullable(),
  asset: z.string().nullable(),
  beginningAdjustmentValue: z.string().nullable(),
  beginningPrice: z.string().nullable(),
  beginningQuantity: z.string().nullable(),
  costBasis: z.string().nullable(),
  endingAdjustmentValue: z.string().nullable(),
  endingPrice: z.string().nullable(),
  endingQuantity: z.string().nullable(),
  lotId: z.string().nullable(),
  periodAdjustment: z.string().nullable(),
  source: z.string().nullable(),
  totalAdjustment: z.string().nullable(),
})

export const fairMarketValueLotLevelSchema = z
  .object({
    rows: z.array(fairMarketValueLotLevelRowSchema),
  })
  .merge(reportPaginationSchema)

export const fairMarketValueSummaryRowSchema = z.object({
  asset: z.string().nullable(),
  beginningadjustmentvalue: z.string().nullable(),
  costbasis: z.string().nullable(),
  date: z.string().nullable(),
  endingadjustmentvalue: z.string().nullable(),
  lowestperiodprice: z.string().nullable(),
  lowestpricedatetime: z.string().nullable(),
  periodadjustment: z.string().nullable(),
  periodbegindate: z.string().nullable(),
  periodenddate: z.string().nullable(),
  quantity: z.string().nullable(),
  source: z.string().nullable(),
  totaladjustment: z.string().nullable(),
})

export const fairMarketValueSummarySchema = z
  .object({
    rows: z.array(fairMarketValueSummaryRowSchema),
  })
  .merge(reportPaginationSchema)

export const qaJournalEntryDetailedRowSchema = z.object({
  account_name: z.string(),
  account_id: z.string(),
  asset: z.string().nullable(),
  asset_amount: z.string(),
  credits_usd: z.string(),
  debits_usd: z.string(),
  memo: z.string(),
  t_timestamp: z.string(),
  transaction_id: z.string(),
  event_type: z.string(),
})

export const qaJournalEntryDetailedSchema = z
  .object({
    rows: z.array(qaJournalEntryDetailedRowSchema),
  })
  .merge(reportPaginationSchema)

export const journalEntryDetailedRowSchema = z.object({
  account_name: z.string(),
  account_id: z.string(),
  asset: z.string().nullable(),
  asset_amount: z.string(),
  credits_usd: z.string(),
  debits_usd: z.string(),
  memo: z.string(),
  t_timestamp: z.string(),
  transaction_id: z.string(),
})

export const journalEntryDetailedSchema = z
  .object({
    rows: z.array(journalEntryDetailedRowSchema),
  })
  .merge(reportPaginationSchema)

export const journalEntrySummaryRowSchema = z.object({
  account_name: z.string(),
  credits_fiat: z.string(),
  debits_fiat: z.string(),
})

export const journalEntrySummarySchema = z
  .object({
    rows: z.array(journalEntrySummaryRowSchema),
  })
  .merge(reportPaginationSchema)

export const balanceRowSchema = z.object({
  address_label: z.string(),
  amount_in: z.string(),
  amount_in_usd: z.string(),
  amount_out: z.string(),
  amount_out_usd: z.string(),
  asset: z.string(),
  category: z.string(),
  id: z.string(),
  realized_gain_loss: z.string().nullable(),
  rolling_balance: z.string(),
  rolling_cost_basis_usd: z.string(),
  t_timestamp: z.string(),
})

export const balanceSchema = z
  .object({
    rows: z.array(balanceRowSchema),
  })
  .merge(reportPaginationSchema)

export const nftRowSchema = z.object({
  address: z.string(),
  address_label: z.string(),
  amount: z.string(),
  asset: z.string(),
  tokenid: z.string(),
})

export const nftSchema = z
  .object({
    rows: z.array(nftRowSchema),
  })
  .merge(reportPaginationSchema)

const rollingEntrySchema = z.object({
  address_source: z.string(),
  asset: z.string(),
  balance_date: z.string(),
  price: z.number(),
  rolling_deposits_amount: z.record(z.number()),
  rolling_deposits_internal_amount: z.record(z.number()),
  rolling_deposits_internal_value: z.record(z.number()),
  rolling_deposits_value: z.record(z.number()),
  rolling_expenses_amount: z.record(z.number()),
  rolling_expenses_internal_amount: z.record(z.number()),
  rolling_expenses_internal_value: z.record(z.number()),
  rolling_expenses_value: z.record(z.number()),
  rolling_fees_amount: z.number(),
  rolling_fees_value: z.number(),
})

const gainLossEntrySchema = z.object({
  address_source: z.string(),
  asset: z.string(),
  balance_date: z.string(),
  rolling_gainloss: z.string(),
})

export const legacyRollForwardSchema = z.object({
  data: z.object({
    cost_basis: z.object({
      start: z.record(z.record(z.number())).nullable(),
      end: z.record(z.record(z.number())),
    }),
    rolling: z.object({
      start: z.record(z.record(rollingEntrySchema)).optional(),
      end: z.record(z.record(rollingEntrySchema)).optional(),
    }),
    rolling_gainloss: z.object({
      start: z.record(z.record(gainLossEntrySchema)).optional(),
      end: z.record(z.record(gainLossEntrySchema)).optional(),
    }),
  }),
  report_type: z.string(),
})

export const transactionEditHistoryRowSchema = z.object({
  created_at: z.string(),
  transaction_id: z.string(),
  transaction_date: z.string(),
  event_type: z.string(),
  value: z.string().nullable(),
  source: z.string(),
  source_id: z.string().nullable(),
  organization: z.string().optional(),
})

export const transactionEditHistorySchema = z
  .object({
    rows: z.array(transactionEditHistoryRowSchema),
  })
  .merge(reportPaginationSchema)

const asc35060ReportRowSchema = z.object({
  key: z.string(),
  fiat_amount: z.string(),
})

const asc35060ReportItemSchema = z.object({
  total: z.string(),
  items: z.array(asc35060ReportRowSchema).optional(),
})

export const asc35060ReportSchema = z.object({
  data: z.object({
    Additions: asc35060ReportItemSchema.optional(),
    Dispositions: asc35060ReportItemSchema.optional(),
    'Fair Value at Period Start': asc35060ReportItemSchema,
    'Fair Value at Period End': asc35060ReportItemSchema,
    'Gain / (Loss)': asc35060ReportItemSchema,
  }),
})
